import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useTransferOrder } from '@/api/rpc/hooks/inventory-management/useTransferOrders';
import { PackageIcon, ArrowRightIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { T, Var, useGT } from 'gt-react';

interface TransferOrderPreviewProps {
  entityType: SystemEntity; // Required by preview component registry but not used - type is always IM_TRANSFER_ORDER
  entityId: string;
  className?: string;
}

// Famous transfer order status mapping (status field is free-form on the model,
// but Famous-sourced TOs use single-character codes mirroring the SO status set).
const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  '0': 'secondary',
  '1': 'secondary',
  '2': 'secondary',
  '3': 'default',
  '4': 'default',
  '5': 'default',
  '6': 'default',
  P: 'default',
  C: 'outline',
  X: 'destructive',
};

export function TransferOrderPreview({
  entityId,
  className,
}: TransferOrderPreviewProps) {
  const gt = useGT();
  const { transferOrder, lineItems, charges, isLoading, error } =
    useTransferOrder(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <PackageIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="transfer order preview title">Transfer Order</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="loading state">Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !transferOrder) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <PackageIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="transfer order preview title">Transfer Order</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="error loading transfer order preview">
            Unable to load transfer order details
          </T>
        </p>
      </div>
    );
  }

  const statusVariant = STATUS_VARIANTS[transferOrder.status] ?? 'outline';
  const sourceName = transferOrder.sourceWarehouse?.name;
  const destinationName = transferOrder.destinationWarehouse?.name;
  const naLabel = gt('N/A', { context: 'not applicable abbreviation' });

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <PackageIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">
          <T>
            TO <Var>{transferOrder.transferNumber}</Var>
          </T>
        </h4>
      </div>

      <div className="space-y-2 text-xs mb-3">
        <div className="flex items-center justify-between">
          <Badge variant={statusVariant} className="text-xs">
            <Var>{String(transferOrder.status)}</Var>
          </Badge>
        </div>

        {(sourceName || destinationName) && (
          <div className="flex items-center gap-1.5">
            <span className="font-medium truncate">
              {sourceName ?? naLabel}
            </span>
            <ArrowRightIcon className="size-3 text-muted-foreground shrink-0" />
            <span className="font-medium truncate">
              {destinationName ?? naLabel}
            </span>
          </div>
        )}

        <div>
          <span className="text-muted-foreground">
            <T context="TO field label">Order Date: </T>
          </span>
          <span className="font-medium">
            {formatDateOnlyString(transferOrder.orderDate)}
          </span>
        </div>

        {transferOrder.shipDate && (
          <div>
            <span className="text-muted-foreground">
              <T context="TO field label">Ship Date: </T>
            </span>
            <span className="font-medium">
              {formatDateOnlyString(transferOrder.shipDate)}
            </span>
          </div>
        )}

        {transferOrder.deliveryDate && (
          <div>
            <span className="text-muted-foreground">
              <T context="TO field label">Delivery Date: </T>
            </span>
            <span className="font-medium">
              {formatDateOnlyString(transferOrder.deliveryDate)}
            </span>
          </div>
        )}

        {charges.length > 0 && (
          <div>
            <span className="text-muted-foreground">
              <T context="TO field label">Charges: </T>
            </span>
            <span className="font-medium">
              <Var>{charges.length}</Var>
            </span>
          </div>
        )}
      </div>

      {lineItems.length > 0 && (
        <div className="border-t pt-2 mt-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              <T context="TO field label">Line Items</T>
            </span>
            <span className="text-xs text-muted-foreground">
              <Var>{lineItems.length}</Var>
            </span>
          </div>
          <ul className="space-y-1">
            {lineItems.slice(0, MAX_LINE_ITEMS_PREVIEW).map((line) => {
              const label =
                line.productName ??
                line.productSku ??
                line.description ??
                naLabel;
              const qty = formatQuantity(line.quantity);
              const uom = line.unitOfMeasure;
              return (
                <li key={line.id} className="flex items-baseline gap-2 text-xs">
                  {line.lineNumber != null && (
                    <span className="text-muted-foreground font-mono shrink-0">
                      <Var>{String(line.lineNumber)}</Var>
                    </span>
                  )}
                  <span className="flex-1 truncate" title={label}>
                    {label}
                  </span>
                  <span className="text-muted-foreground shrink-0 tabular-nums">
                    <Var>{qty}</Var>
                    {uom ? <Var> {uom}</Var> : null}
                  </span>
                </li>
              );
            })}
          </ul>
          {lineItems.length > MAX_LINE_ITEMS_PREVIEW && (
            <p className="text-xs text-muted-foreground mt-1.5">
              <T>
                +<Var>{lineItems.length - MAX_LINE_ITEMS_PREVIEW}</Var> more
              </T>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const MAX_LINE_ITEMS_PREVIEW = 5;

function formatQuantity(value: string): string {
  // Operate on the raw DECIMAL(18,4) string to avoid Number precision loss and
  // Intl rounding. We truncate (not round) the fractional part to 4 digits,
  // drop trailing zeros, and insert thousands separators in the integer part.
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(value);
  if (!match) return value;
  const [, sign, intPart, fracPart = ''] = match;
  const truncatedFrac = fracPart.slice(0, 4).replace(/0+$/, '');
  const intWithSeparators = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return truncatedFrac
    ? `${sign}${intWithSeparators}.${truncatedFrac}`
    : `${sign}${intWithSeparators}`;
}
